<script setup lang="ts">
import { useVModel } from '@vueuse/core';
import { Pencil, Trash2, ChevronDown } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

const props = defineProps<{
    modelValue?: string | undefined;
    class?: string;
}>();

const emit = defineEmits<{
    (e: 'update:modelValue', value: string | undefined): void;
}>();

const note = useVModel(props, 'modelValue', emit, {
    passive: true,
    defaultValue: undefined,
});

const clearNote = () => {
    if (!confirm('Are you sure you want to clear the order note?')) {
        return;
    }
    note.value = '';
};

const setNote = (v: string | number) => {
    note.value = String(v);
};
</script>

<template>
    <Collapsible :class="cn('my-2 min-w-0 w-full max-w-full border rounded-md overflow-hidden', props.class)">
        <CollapsibleTrigger
            class="w-full min-w-0 px-4 py-3 flex items-center gap-2 bg-background hover:bg-muted/50 cursor-pointer">
            <Pencil class="flex-shrink-0" />
            <div
                class="max-w-full overflow-x-hidden whitespace-nowrap text-ellipsis overflow-hidden text-xl flex-1 text-left">
                {{ note || 'Add a note' }}
            </div>
            <ChevronDown class="ml-2 flex-shrink-0 transition-transform duration-200" />
        </CollapsibleTrigger>
        <CollapsibleContent>
            <div class="min-w-0 max-w-full p-0">
                <Textarea :model-value="note ?? ''"
                          rows="7"
                          class="min-w-0 max-w-full rounded-none break-all"
                          maxlength="510"
                          placeholder="your notes here..."
                          @update:model-value="setNote" />
                <Button variant="outline"
                        class="w-full rounded-none"
                        @click="clearNote()">
                    <Trash2 />
                    Clear
                </Button>
            </div>
        </CollapsibleContent>
    </Collapsible>
</template>
